/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {getConfig} from 'pwa-kit-runtime/utils/ssr-config'
import React, {useEffect, useMemo, useState, useRef, useCallback} from 'react'
import PropTypes from 'prop-types'
import {useParams} from 'react-router-dom'
import {FormattedMessage, useIntl} from 'react-intl'
import {Helmet} from 'react-helmet'

// Components
import {
    Box,
    Flex,
    SimpleGrid,
    Text,
    Stack,
    useDisclosure,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalContent,
    ModalCloseButton,
    ModalOverlay,
    Container
} from '@chakra-ui/react'
import BackToTopLink from '../../components/back-to-top-link'
import Breadcrumb from '../../components/breadcrumb'

// Project Components
import ProductTile, {Skeleton as ProductTileSkeleton} from '../../components/product-tile'
import {getPathParamsSplit} from '../../utils/url'
import FiltersSection from './partials/filters-section'
import Refinements from './partials/refinements'
import EmptyResults from './partials/empty-results'
import PageHeader from './partials/page-header'
import ProductRecommendations from './partials/product-recommendations'
import CMSContent from '../../components/cms-content'
import ContentSlot from '../../components/content-slot'

// Hooks
import {useSearchParams} from '../../hooks'
import {parse as parseSearchParams} from '../../hooks/use-search-params'
import useEinstein from '../../commerce-api/hooks/useEinstein'
import useInteractionStudioPixel from '../../tracking/interaction-studio'
import {preloadCmsContent} from '../../components/cms-content/preload-cms-content'
import useTrackViewList from '../../components/recommended-products/hooks/use-track-view-list'

// Others
import {HTTPNotFound} from 'pwa-kit-react-sdk/ssr/universal/errors'
import {trackSelectItem, trackEvent} from '../../tracking/analytics'

// Constants
import {
    API_ERROR_MESSAGE,
    DEFAULT_SEARCH_PARAMS,
    DEFAULT_CATEGORY_PARAMS,
    CATEGORY_BANNER_SLOT_ID,
    HYBRID_SEARCH,
    HYBRID_PLP,
    HIDDEN_REFINEMENTS
} from '../../constants'
import useNavigation from '../../hooks/use-navigation'
import useHybridURL from '../../hooks/use-hybrid-url'
import LoadingSpinner from '../../components/loading-spinner'
import {useInitialDataLayer} from '../../tracking/hooks/use-initial-data-layer'
import PaginationLegend from './partials/pagination-legend'
import {useCommerceAPI} from '../../commerce-api/contexts'
import {useToast} from '../../hooks/use-toast'
import PlpSubnavigation from './partials/subnavigation'
import Seo from '../../components/seo'
import useEffectEvent from '../../hooks/use-effect-event'

// NOTE: You can ignore certain refinements on a template level by updating the below
// list of ignored refinements.
const REFINEMENT_DISALLOW_LIST = ['c_isNew']

/*
 * This is a simple product listing page. It displays a paginated list
 * of product hit objects. Allowing for sorting and filtering based on the
 * allowable filters and sort refinements.
 */
const ProductList = (props) => {
    const {
        searchQuery,
        productSearchResult,
        promotions,
        category,
        // eslint-disable-next-line react/prop-types
        location,
        isLoading,
        velocityContentSlotPreloaded,
        forcelisting
    } = props
    const velocityContentSlotId = category?.c_velocityAssetID
    const {isOpen, onOpen, onClose} = useDisclosure()
    const {formatMessage, locale} = useIntl()
    const hybridURL = useHybridURL()
    // use router navigation for query parameters update
    // noinspection JSDeprecatedSymbols
    const navigate = useNavigation()
    const params = useParams()
    const toast = useToast()
    const einstein = useEinstein()
    const api = useCommerceAPI()
    const filtersRef = useRef(null)
    const isSearch = !category
    const trackingLabel = isSearch ? 'search results' : `PLP ${category.name}`

    const convertHits = (hits) => {
        return (
            hits?.map((hit) => ({
                ...hit,
                name: hit.name || hit.c_productName,
                imageGroups: hit.imageGroups || hit.c_imageGroups || [],
                variants: hit.variants || hit.c_variants || [],
                orderable: hit.productType !== 'master' ? hit.c_orderable : hit.orderable,
                productPromotions: [{promotionalPrice: hit.c_promotionalPrice || null}],
                validFrom: hit.validFrom || hit.c_validFrom,
                brand: hit.brand || hit.c_brand || null
            })) || []
        )
    }

    const [hits, setHits] = useState(convertHits(productSearchResult?.hits) || [])

    useInitialDataLayer({
        pageType: isSearch ? 'Search Result' : 'Product List'
    })
    useInteractionStudioPixel()

    const basePath = `${location.pathname}${location.search}`

    useEffect(() => {
        setHits(convertHits(productSearchResult?.hits))
    }, [productSearchResult?.hits])

    const scrollFiltersSectionToView = () => {
        filtersRef?.current?.scrollIntoView?.()
    }

    useEffect(() => {
        if (forcelisting && window.type !== 'undefined') {
            scrollFiltersSectionToView()
            window.addEventListener('load', scrollFiltersSectionToView)
        }

        return () => {
            window.removeEventListener('load', scrollFiltersSectionToView)
        }
    }, [forcelisting])

    useTrackViewList({
        products: hits,
        trackingLabel
    })

    // If we are loaded and still have no products, show the no results component.
    const showNoResults = !isLoading && productSearchResult && !hits?.length

    if (productSearchResult?.searchPhraseSuggestions?.c_redirectURL) {
        let redirectURL = productSearchResult?.searchPhraseSuggestions?.c_redirectURL
        if (redirectURL.indexOf(locale.toLowerCase()) > 0) {
            redirectURL = redirectURL.substring(
                redirectURL.indexOf(locale.toLowerCase()) + locale.length
            )
        }
        hybridURL(isSearch ? HYBRID_SEARCH : HYBRID_PLP, redirectURL, {}, redirectURL)
    }

    /**************** Wishlist ****************/
    // const wishlist = useWishlist()
    // // keep track of the items has been add/remove to/from wishlist
    // const [wishlistLoading, setWishlistLoading] = useState([])
    // // TODO: DRY this handler when intl provider is available globally
    // const addItemToWishlist = async (product) => {
    //     try {
    //         setWishlistLoading([...wishlistLoading, product.productId])
    //         await wishlist.createListItem({
    //             id: product.productId,
    //             quantity: 1
    //         })
    //         toast({
    //             title: formatMessage(TOAST_MESSAGE_ADDED_TO_WISHLIST, {quantity: 1}),
    //             action: (
    //                 // it would be better if we could use <Button as={Link}>
    //                 // but unfortunately the Link component is not compatible
    //                 // with Chakra Toast, since the ToastManager is rendered via portal
    //                 // and the toast doesn't have access to intl provider, which is a
    //                 // requirement of the Link component.
    //                 <Button variant="link" onClick={() => navigateHybrid(HYBRID_ACCOUNT, '/account/wishlist')}>
    //                     {formatMessage(TOAST_ACTION_VIEW_WISHLIST)}
    //                 </Button>
    //             )
    //         })
    //     } catch {
    //         toast({
    //             title: formatMessage(API_ERROR_MESSAGE),
    //             status: 'error'
    //         })
    //     } finally {
    //         setWishlistLoading(wishlistLoading.filter((id) => id !== product.productId))
    //     }
    // }
    //
    // // TODO: DRY this handler when intl provider is available globally
    // const removeItemFromWishlist = async (product) => {
    //     try {
    //         setWishlistLoading([...wishlistLoading, product.productId])
    //         await wishlist.removeListItemByProductId(product.productId)
    //         toast({
    //             title: formatMessage(TOAST_MESSAGE_REMOVED_FROM_WISHLIST)
    //         })
    //     } catch {
    //         toast({
    //             title: formatMessage(API_ERROR_MESSAGE),
    //             status: 'error'
    //         })
    //     } finally {
    //         setWishlistLoading(wishlistLoading.filter((id) => id !== product.productId))
    //     }
    // }

    /**************** Einstein ****************/
    const onProductSearchResultsChange = useEffectEvent((productSearchResult) =>
        isSearch
            ? einstein.sendViewSearch(searchQuery, productSearchResult)
            : einstein.sendViewCategory(category, productSearchResult)
    )
    useEffect(() => {
        if (productSearchResult) {
            onProductSearchResultsChange(productSearchResult)
        }
    }, [productSearchResult, onProductSearchResultsChange])

    /**************** Filters ****************/
    const [searchParams, {stringify: stringifySearchParams}] = useSearchParams()
    const [isLoadingMore, setIsLoadingMore] = useState(false)

    const disableLoadMore = useMemo(() => {
        return isLoadingMore || productSearchResult?.total <= hits.length
    }, [productSearchResult, isLoadingMore, hits])

    const hasSelectedRefinements = useMemo(
        () =>
            Object.entries(productSearchResult?.selectedRefinements || {}).some(
                ([key, value]) => !HIDDEN_REFINEMENTS.includes(key) && value
            ),
        [productSearchResult]
    )

    const loadMore = async () => {
        setIsLoadingMore(true)
        const offset = (searchParams?.offset || 0) + (hits?.length || 0)

        const newSearchParams = {
            ...searchParams,
            ...parseSearchParams(location.search, false),
            offset: offset
        }

        if (!newSearchParams.refine.includes(`cgid=${category?.id}`) && category?.id) {
            newSearchParams.refine.push(`cgid=${category?.id}`)
        }
        try {
            const nextSearchResult = await api.shopperSearch.productSearch({
                parameters: newSearchParams
            })
            setHits((currentHits) => [...currentHits, ...convertHits(nextSearchResult?.hits)])
        } catch {
            toast({
                title: formatMessage(API_ERROR_MESSAGE),
                status: 'error'
            })
        } finally {
            setIsLoadingMore(false)
        }
    }

    // Toggles filter on and off
    const toggleFilter = useCallback(
        (value, attributeId, selected, allowMultiple = true) => {
            const searchParamsCopy = {...searchParams}

            // Reset the `offset` search param
            // NOTE: it's necessary to have it as useSortUrls always includes offset and without produces different urls
            searchParamsCopy.offset = 0

            // If we aren't allowing for multiple selections, simply clear any value set for the
            // attribute, and apply a new one if required.
            if (!allowMultiple) {
                const previousValue = searchParamsCopy.refine[attributeId]
                delete searchParamsCopy.refine[attributeId]

                // Note the loose comparison, for "string != number" checks.
                if (!selected && value.value != previousValue) {
                    searchParamsCopy.refine[attributeId] = value.value
                }
            } else {
                // Get the attribute value as an array.
                let attributeValue = searchParamsCopy.refine[attributeId] || []

                // Ensure that the value is still converted into an array if it's a `string` or `number`.
                if (typeof attributeValue === 'string') {
                    attributeValue = attributeValue.split('|')
                } else if (typeof attributeValue === 'number') {
                    attributeValue = [attributeValue]
                }

                // Either set the value, or filter the value out.
                if (!selected) {
                    if (!attributeValue.includes(value.value)) {
                        attributeValue.push(value.value)
                    }
                } else {
                    attributeValue = attributeValue?.filter((v) => v !== value.value)
                }

                // Update the attribute value in the new search params.
                searchParamsCopy.refine[attributeId] = attributeValue.join('|')

                // If the update value is an empty array, remove the current attribute key.
                if (searchParamsCopy.refine[attributeId].length === 0) {
                    delete searchParamsCopy.refine[attributeId]
                }
            }

            trackEvent({
                event: 'filter',
                content_type: searchQuery ? 'search' : 'PLP',
                filter_action: 'apply_filter',
                filter: Object.entries(searchParamsCopy.refine).map(
                    ([filter_category, filter_id]) => ({
                        filter_category,
                        filter_id
                    })
                )
            })

            if (!searchQuery) {
                navigate(
                    `/category/${params.categoryPath}?${stringifySearchParams(searchParamsCopy)}`
                )
            } else {
                navigate(`/search?${stringifySearchParams(searchParamsCopy)}`)
            }
        },
        [navigate, params?.categoryPath, searchParams, searchQuery, stringifySearchParams]
    )

    // Clears all filters
    const resetFilters = useCallback(() => {
        trackEvent({
            event: 'filter',
            content_type: searchQuery ? 'search' : 'PLP',
            filter_action: 'clear_all_filter'
        })

        if (searchQuery) {
            navigate(`/search?q=${searchQuery}`)
        } else {
            navigate(getPathParamsSplit(window.location).pathname)
        }
    }, [navigate, searchQuery])

    let selectedSortingOptionLabel = productSearchResult?.sortingOptions?.find(
        (option) => option.id === productSearchResult?.selectedSortingOption
    )

    // API does not always return a selected sorting order
    if (!selectedSortingOptionLabel) {
        selectedSortingOptionLabel = productSearchResult?.sortingOptions?.[0]
    }

    const onBackToTopLinkClick = () => {
        // focus first button of the page
        document.querySelectorAll('button')?.[0]?.focus()
    }

    const filtersProps = useMemo(
        () => ({
            basePath,
            hasSelectedRefinements,
            productSearchResult,
            onOpen,
            onResetFilters: resetFilters,
            onToggleFilter: toggleFilter,
            searchQuery
        }),
        [
            basePath,
            hasSelectedRefinements,
            productSearchResult,
            onOpen,
            resetFilters,
            toggleFilter,
            searchQuery
        ]
    )

    return (
        <Box className="sf-product-list-page" data-testid="sf-product-list-page">
            {isSearch ? (
                <Seo
                    title={formatMessage({
                        id: 'global.page.default_site_title',
                        defaultMessage:
                            'Stokke® Online Shop | Children´s High Chair, Strollers & Nursery'
                    })}
                />
            ) : (
                <Seo
                    title={category?.pageTitle}
                    description={category?.pageDescription}
                    keywords={category?.pageKeywords}
                />
            )}
            <Helmet>
                {!isSearch && <meta property="evg:categoryId" content={category?.id} />}
                {!isSearch && <meta property="evg:categoryName" content={category?.name} />}
                {!isSearch && <meta property="evg:pageType" content={'category-page'} />}
                {isSearch ? (
                    <meta name="robots" content="noindex" />
                ) : (
                    <link rel="canonical" href={category?.c_canonicalUrl} />
                )}
            </Helmet>
            {showNoResults ? (
                <EmptyResults
                    searchQuery={searchQuery}
                    suggestions={productSearchResult?.searchPhraseSuggestions?.suggestedPhrases}
                    category={category}
                    filtersRef={filtersRef}
                    filtersProps={filtersProps}
                    hasSelectedRefinements={hasSelectedRefinements}
                />
            ) : (
                <>
                    {/* Content Slot - Search result top banner */}
                    {searchQuery && (
                        <ContentSlot
                            slotId="search-result-banner-top"
                            showError={false}
                            variant="cms"
                            disableSkeleton={true}
                        />
                    )}

                    {/* Header for search */}
                    {searchQuery && (
                        <Container layerStyle="page">
                            <PageHeader
                                searchQuery={searchQuery}
                                productSearchResult={productSearchResult}
                                isLoading={isLoading}
                            />
                        </Container>
                    )}

                    {/* Cat banner content slot */}
                    {category?.id && (
                        <ContentSlot
                            slotId={CATEGORY_BANNER_SLOT_ID}
                            context={category.id}
                            showError={false}
                            variant="cms"
                        />
                    )}
                    {/* PLP Velocity Content slot */}
                    {velocityContentSlotId && (
                        <CMSContent
                            content={velocityContentSlotPreloaded}
                            id={velocityContentSlotId}
                            showError={false}
                        />
                    )}

                    {/* Breadcrumbs */}
                    {category && (
                        <Container layerStyle="page" py="2" px={{base: 6, xl: 8}}>
                            <Breadcrumb categories={category?.parentCategoryTree} />
                        </Container>
                    )}
                    {/* FiltersSection */}
                    <PlpSubnavigation filtersRef={filtersRef}>
                        <FiltersSection {...filtersProps} />
                    </PlpSubnavigation>
                    <Container ref={filtersRef} layerStyle="page" variant="no-padding">
                        <FiltersSection {...filtersProps} />
                    </Container>

                    {/* Body  */}
                    <Container layerStyle="page" padding={{base: '1rem', lg: '1rem 2rem'}}>
                        <Flex direction="column" gap={6}>
                            {searchQuery && (
                                <Box textAlign="center">
                                    <Text textStyle="Regular XLarge">
                                        {formatMessage(
                                            {
                                                id: 'product_list.total_text',
                                                defaultMessage:
                                                    '{total} {total, plural, one {product} other {products}}'
                                            },
                                            {total: productSearchResult?.total}
                                        )}
                                    </Text>
                                </Box>
                            )}

                            <SimpleGrid
                                columns={{base: 2, lg: 4}}
                                spacing={{base: 4, lg: 6}}
                                gridAutoFlow="dense"
                            >
                                {isLoading || !productSearchResult
                                    ? new Array(searchParams.limit)
                                          .fill(0)
                                          .map((value, index) => (
                                              <ProductTileSkeleton key={index} />
                                          ))
                                    : hits.map((productSearchItem, index) => {
                                          return (
                                              <ProductTile
                                                  data-testid={`sf-product-tile-${productSearchItem.productId}`}
                                                  key={index}
                                                  product={productSearchItem}
                                                  dynamicImageProps={{
                                                      widths: [
                                                          '50vw',
                                                          '50vw',
                                                          '20vw',
                                                          '20vw',
                                                          '25vw'
                                                      ]
                                                  }}
                                                  propagateDisabled={true}
                                                  promotions={promotions}
                                                  variant={
                                                      productSearchItem?.hitType === 'master'
                                                          ? 'plp-unsliced'
                                                          : 'plp-sliced'
                                                  }
                                                  onClick={() => {
                                                      trackSelectItem(
                                                          productSearchItem,
                                                          null,
                                                          trackingLabel
                                                      )
                                                  }}
                                              />
                                          )
                                      })}
                            </SimpleGrid>
                        </Flex>
                    </Container>
                    {/* Footer */}
                    <Container layerStyle="page" variant="no-padding">
                        <Stack
                            spacing={3}
                            alignItems="center"
                            paddingTop={4}
                            paddingBottom={{base: 8, lg: 10}}
                        >
                            <PaginationLegend
                                offset={productSearchResult?.offset}
                                total={productSearchResult?.total}
                                resultsLength={hits.length}
                            />
                            <Button
                                variant="outline"
                                width="15.25rem"
                                onClick={loadMore}
                                disabled={disableLoadMore}
                            >
                                <FormattedMessage defaultMessage="Load more" id="global.loadmore" />
                            </Button>
                        </Stack>
                    </Container>
                    {/*  Recommendation  */}
                    <ProductRecommendations />

                    {/* Content Slot - Search result bottom banner */}
                    {searchQuery && (
                        <ContentSlot
                            slotId="search-result-banner-bottom"
                            showError={false}
                            variant="cms"
                        />
                    )}

                    {/* Back to top */}
                    <Flex background="stokkeCore.white" paddingY={6} justifyContent="center">
                        <BackToTopLink scrollToRef={filtersRef} onClick={onBackToTopLinkClick} />
                    </Flex>
                </>
            )}
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                motionPreset="slideInBottom"
                scrollBehavior="inside"
                variant="plp"
                colorScheme=""
            >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <Text display={{lg: 'none'}} textStyle="Regular 2XLarge">
                            <FormattedMessage
                                defaultMessage="Filter & Sort"
                                id="global.filter_and_sort"
                            />
                        </Text>
                        <Text display={{base: 'none', lg: 'block'}} textStyle="Regular 2XLarge">
                            <FormattedMessage
                                display={{base: 'none', lg: 'block'}}
                                defaultMessage="Filter"
                                id="product_list.label.filter"
                            />
                        </Text>
                        <Text textStyle="Regular Medium" color="stokkeGray.accessibleText">
                            {formatMessage(
                                {
                                    id: 'product_list.total_text',
                                    defaultMessage:
                                        '{total} {total, plural, one {product} other {products}}'
                                },
                                {total: productSearchResult?.total}
                            )}
                        </Text>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {isLoading && <LoadingSpinner />}
                        <Refinements
                            basePath={basePath}
                            productSearchResult={productSearchResult}
                            toggleFilter={toggleFilter}
                            filters={productSearchResult?.refinements}
                            selectedFilters={productSearchResult?.selectedRefinements}
                            searchQuery={searchQuery}
                        />
                    </ModalBody>

                    <ModalFooter>
                        <Flex direction="column" align="center" gap="6">
                            <Button variant="link" onClick={() => resetFilters()}>
                                <FormattedMessage
                                    defaultMessage="Clear Filters"
                                    id="product_list.modal.button.clear_filters"
                                />
                            </Button>
                            <Button width="full" onClick={onClose}>
                                {formatMessage(
                                    {
                                        id: 'product_list.modal.button.view_items',
                                        defaultMessage: 'View {count} items'
                                    },
                                    {
                                        count: productSearchResult?.total
                                    }
                                )}
                            </Button>
                        </Flex>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    )
}

ProductList.getTemplateName = () => 'product-list'

ProductList.shouldGetProps = ({previousLocation, location}) =>
    !previousLocation ||
    previousLocation.pathname !== location.pathname ||
    previousLocation.search !== location.search

ProductList.getProps = async ({res, params, location, api}) => {
    const {categoryPath} = params
    const urlParams = new URLSearchParams(location.search)
    const searchQuery = urlParams.get('q')
    const forcelisting = urlParams.get('forcelisting')

    // In case somebody navigates to /search without a param
    if (!categoryPath && !searchQuery) {
        // We will simulate search for empty string
        return {searchQuery: ' ', productSearchResult: {}}
    }

    const searchParams = {
        ...(searchQuery ? DEFAULT_SEARCH_PARAMS : DEFAULT_CATEGORY_PARAMS),
        ...parseSearchParams(location.search, false)
    }

    let categoryId

    if (categoryPath) {
        const resolverResult = await api.customObjects.getPlpCategoryId({
            parameters: {path: location.pathname}
        })
        const resolverUrlParams = resolverResult?.c_result?.urlParams

        if (resolverUrlParams && resolverUrlParams.length) {
            const foundParam = resolverUrlParams?.find((param) => param.cgid)

            categoryId = foundParam ? foundParam.cgid : null
        }

        // THIS IS FOR TESTING ONLY - NEEDS TO BE REMOVED BEFORE GOLIVE
        // this removes /category/ path element as this can not be
        // configured yet on all instances
        if (!categoryId) {
            const resolverResultTest = await api.customObjects.getPlpCategoryId({
                parameters: {path: location.pathname.replace('/category/', '/')}
            })
            const resolverUrlParamsTest = resolverResultTest?.c_result?.urlParams

            if (resolverUrlParamsTest && resolverUrlParamsTest.length) {
                const foundParamTest = resolverUrlParamsTest?.find((param) => param.cgid)

                categoryId = foundParamTest ? foundParamTest.cgid : null
            }
        }
        // END OF TEST BLOCK

        if (!categoryId) {
            throw new HTTPNotFound(`Could not find ID for category with path: ${location.pathname}`)
        }

        if (!searchParams.refine.includes(`cgid=${categoryId}`)) {
            searchParams.refine.push(`cgid=${categoryId}`)
        }
    }

    // Set the `cache-control` header values to align with the Commerce API settings.
    if (res) {
        const cacheConfig = getConfig().app.cache
        if (cacheConfig?.plp) {
            res.set('Cache-Control', cacheConfig.plp)
        }
    }

    const [category, productSearchResult] = await Promise.all([
        !categoryPath
            ? Promise.resolve()
            : api.shopperProducts.getCategory({
                  parameters: {id: categoryId, levels: 0}
              }),
        api.shopperSearch.productSearch({
            parameters: searchParams
        })
    ])

    // Apply disallow list to refinements.
    productSearchResult.refinements = productSearchResult?.refinements?.filter(
        ({attributeId}) => !REFINEMENT_DISALLOW_LIST.includes(attributeId)
    )

    // The `isomorphic-sdk` returns error objects when they occur, so we
    // need to check the category type and throw if required.
    if (category?.type?.endsWith('category-not-found')) {
        throw new HTTPNotFound(category.detail)
    }

    const velocityContentSlotPreloaded =
        category?.c_velocityAssetID && (await preloadCmsContent(category.c_velocityAssetID, {api}))

    let newPromotionIds = []
    productSearchResult?.hits?.forEach((product) => {
        product?.productPromotions?.forEach(({promotionId}) => {
            if (promotionId && newPromotionIds.indexOf(promotionId) < 0) {
                newPromotionIds.push(promotionId)
            }
        })
    })
    const data =
        newPromotionIds && newPromotionIds.length > 0
            ? await api.shopperPromotions.getPromotions({
                  parameters: {ids: newPromotionIds.join(',')}
              })
            : undefined
    const promotions = data?.data || []

    return {
        searchQuery,
        productSearchResult,
        promotions,
        category,
        velocityContentSlotPreloaded,
        forcelisting: !!forcelisting
    }
}

ProductList.propTypes = {
    /**
     * The search result object showing all the product hits, that belong
     * in the supplied category.
     */
    productSearchResult: PropTypes.object,
    /*
     * Indicated that `getProps` has been called but has yet to complete.
     *
     * Notes: This prop is internally provided.
     */
    isLoading: PropTypes.bool,
    /*
     * Object that represents the current location, it consists of the `pathname`
     * and `search` values.
     *
     * Notes: This prop is internally provided.
     */
    location: PropTypes.object,
    promotions: PropTypes.array,
    searchQuery: PropTypes.string,
    onAddToWishlistClick: PropTypes.func,
    onRemoveWishlistClick: PropTypes.func,
    category: PropTypes.object,
    velocityContentSlotPreloaded: PropTypes.object,
    forcelisting: PropTypes.bool
}

export default ProductList
